import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Clock,
  ExternalLink,
  KeyRound,
  Link2,
  ShieldAlert,
  ShieldOff,
  ShieldPlus,
  Upload,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { api } from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import ClientPortalAccessManager from "../../components/client-portal-admin/ClientPortalAccessManager";

const MSG_ADMIN = "Action réservée administrateur";

function getApiErrorMessage(e: unknown, fallback: string): string {
  const ax = e instanceof AxiosError ? e : null;
  const d = ax?.response?.data as { message?: string } | undefined;
  return d?.message?.trim() || fallback;
}

type Summary = {
  clientsAvecAccesPortail?: number;
  accesDesactives?: number;
  demandesEnAttente?: number;
  urgencesClientEnAttente?: number;
  preuvesPaiementEnAttente?: number;
};

type PortalClientRow = {
  id: number;
  nomAffiche: string;
  telephone: string;
  email: string;
  accesPortail: boolean;
  derniereConnexion: string | null;
  abonnementActif: boolean;
  zoneLabel?: string | null;
  liaisonsPortail?: number;
  demandesEnAttente: number;
};

type OrphanPortalUser = {
  id: number;
  email: string | null;
  nom: string;
  prenom: string | null;
};

type LinkedPortalUser = {
  id: number;
  email: string | null;
  nom: string;
  actif: boolean;
};

export default function PortailClientAdminPage(): React.JSX.Element {
  const { user } = useAuth();
  const isAdmin = String(user?.role ?? "").toLowerCase() === "admin";

  const [summary, setSummary] = useState<Summary>({});
  const [rows, setRows] = useState<PortalClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);

  const [accessManagerOpen, setAccessManagerOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkClientId, setLinkClientId] = useState<number | null>(null);
  const [orphans, setOrphans] = useState<OrphanPortalUser[]>([]);
  const [linkedPortalUsers, setLinkedPortalUsers] = useState<LinkedPortalUser[]>([]);
  const [pickUserId, setPickUserId] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createNom, setCreateNom] = useState("");
  const [createPass, setCreatePass] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  const [resetTargetId, setResetTargetId] = useState<number | null>(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetPwdConfirm, setResetPwdConfirm] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get<Summary>("/client-portal/admin/summary");
      setSummary(data);
    } catch {
      toast.error("Impossible de charger le résumé portail client.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTable = useCallback(async () => {
    setTableLoading(true);
    try {
      const { data } = await api.get<{ items?: PortalClientRow[] }>(
        "/client-portal/admin/clients",
        { params: { limit: 50, page: 1 } }
      );
      setRows(data.items ?? []);
    } catch {
      toast.error("Impossible de charger la liste des clients portail.");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSummary();
    void loadTable();
  }, [loadSummary, loadTable]);

  useEffect(() => {
    if (!linkModalOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ items?: OrphanPortalUser[] }>(
          "/client-portal/admin/orphan-portal-users"
        );
        if (!cancelled) setOrphans(data.items ?? []);
      } catch {
        if (!cancelled) toast.error("Liste des comptes CLIENT orphelins indisponible.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkModalOpen]);

  useEffect(() => {
    if (!linkModalOpen || !linkClientId) {
      setLinkedPortalUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{ items?: LinkedPortalUser[] }>(
          `/client-portal/admin/clients/${linkClientId}/portal-users`
        );
        if (!cancelled) setLinkedPortalUsers(data.items ?? []);
      } catch {
        if (!cancelled) toast.error("Comptes liés à ce client introuvables.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkModalOpen, linkClientId]);

  const openLinkModal = (clientId?: number) => {
    setLinkModalOpen(true);
    setLinkClientId(clientId ?? rows[0]?.id ?? null);
    setPickUserId("");
    setCreateEmail("");
    setCreateNom("");
    setCreatePass("");
  };

  const submitLinkExisting = async () => {
    const uid = Number(pickUserId);
    if (!linkClientId || !uid) {
      toast.warning("Choisissez un utilisateur CLIENT à associer.");
      return;
    }
    setLinkBusy(true);
    try {
      await api.post(`/client-portal/admin/clients/${linkClientId}/link-portal-user`, {
        userId: uid,
      });
      toast.success("Compte portail lié");
      setPickUserId("");
      const { data } = await api.get<{ items?: OrphanPortalUser[] }>(
        "/client-portal/admin/orphan-portal-users"
      );
      setOrphans(data.items ?? []);
      const { data: linked } = await api.get<{ items?: LinkedPortalUser[] }>(
        `/client-portal/admin/clients/${linkClientId}/portal-users`
      );
      setLinkedPortalUsers(linked.items ?? []);
      await loadTable();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      const err = ax?.response?.data as { error?: string } | undefined;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 409 && err?.error === "CLIENT_PORTAL_ALREADY_EXISTS") {
        toast.error("Ce client possède déjà un compte portail.");
      } else {
        toast.error("Liaison impossible.");
      }
    } finally {
      setLinkBusy(false);
    }
  };

  const submitCreatePortalUser = async () => {
    if (!linkClientId || !createEmail.trim() || !createNom.trim() || createPass.length < 6) {
      toast.warning("E-mail, nom et mot de passe (6 caractères min.) requis.");
      return;
    }
    setLinkBusy(true);
    try {
      await api.post(`/client-portal/admin/clients/${linkClientId}/create-portal-user`, {
        email: createEmail.trim(),
        nom: createNom.trim(),
        motDePasse: createPass,
      });
      toast.success("Compte portail créé");
      setCreateEmail("");
      setCreateNom("");
      setCreatePass("");
      const { data } = await api.get<{ items?: OrphanPortalUser[] }>(
        "/client-portal/admin/orphan-portal-users"
      );
      setOrphans(data.items ?? []);
      const { data: linked } = await api.get<{ items?: LinkedPortalUser[] }>(
        `/client-portal/admin/clients/${linkClientId}/portal-users`
      );
      setLinkedPortalUsers(linked.items ?? []);
      await loadTable();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      const err = ax?.response?.data as { error?: string } | undefined;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 409 && err?.error === "CLIENT_PORTAL_ALREADY_EXISTS") {
        toast.error("Ce client possède déjà un compte portail.");
      } else if (ax?.response?.status === 409) {
        toast.error("Cet e-mail est déjà utilisé.");
      } else {
        toast.error("Création impossible.");
      }
    } finally {
      setLinkBusy(false);
    }
  };

  const setAccess = async (id: number, active: boolean) => {
    setActionId(id);
    try {
      await api.patch(`/client-portal/admin/clients/${id}/access`, { active });
      toast.success(active ? "Accès activé" : "Accès désactivé");
      await loadSummary();
      await loadTable();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 404) {
        toast.error("Aucun compte portail lié à ce client.");
      } else {
        toast.error("Action impossible.");
      }
    } finally {
      setActionId(null);
    }
  };

  const openResetModal = (id: number) => {
    setResetTargetId(id);
    setResetPwd("");
    setResetPwdConfirm("");
  };

  const submitResetModal = async () => {
    if (!resetTargetId) return;
    const p = resetPwd.trim();
    const p2 = resetPwdConfirm.trim();
    if (p.length < 6) {
      toast.warning("Mot de passe trop court (min. 6 caractères).");
      return;
    }
    if (p !== p2) {
      toast.warning("Les deux saisies du mot de passe ne correspondent pas.");
      return;
    }
    setActionId(resetTargetId);
    try {
      await api.post(`/client-portal/admin/clients/${resetTargetId}/reset-password`, {
        temporaryPassword: p,
      });
      toast.success("Mot de passe réinitialisé");
      setResetTargetId(null);
      setResetPwd("");
      setResetPwdConfirm("");
      await loadSummary();
      await loadTable();
    } catch (e) {
      const ax = e instanceof AxiosError ? e : null;
      if (ax?.response?.status === 403) {
        toast.error(getApiErrorMessage(e, MSG_ADMIN));
      } else if (ax?.response?.status === 404) {
        toast.error("Aucun compte portail pour ce client.");
      } else if (ax?.response?.status === 501) {
        toast.warning("Fonction à connecter côté serveur.");
      } else {
        toast.error("Réinitialisation impossible.");
      }
    } finally {
      setActionId(null);
    }
  };

  const kpis = [
    {
      label: "Clients avec accès portail",
      value: summary.clientsAvecAccesPortail ?? "—",
      icon: Users,
    },
    {
      label: "Accès désactivés",
      value: summary.accesDesactives ?? "—",
      icon: ShieldOff,
    },
    {
      label: "Demandes en attente",
      value: summary.demandesEnAttente ?? "—",
      icon: Clock,
    },
    {
      label: "Urgences client",
      value: summary.urgencesClientEnAttente ?? "—",
      icon: ShieldAlert,
    },
    {
      label: "Preuves paiement en attente",
      value: summary.preuvesPaiementEnAttente ?? "—",
      icon: Upload,
    },
  ];

  const clientOrigin = window.location.origin;
  const openPortal = () => {
    window.open(`${clientOrigin}/client/login`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-full space-y-6 text-slate-100">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-emerald-400/90">
            Administration
          </p>
          <h1 className="text-2xl font-semibold text-white">Portail client</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Pilotez les accès B2C, les demandes issues du portail et l’activité — distinct du compte
            staff (admin).
          </p>
        </div>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {kpis.map((k) => (
          <article
            key={k.label}
            className="rounded-xl border border-white/10 bg-slate-900/80 p-4 shadow-lg shadow-black/30 backdrop-blur"
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-400">{k.label}</p>
              <k.icon className="h-4 w-4 text-emerald-400/80" aria-hidden />
            </div>
            <p className="mt-3 text-2xl font-semibold tabular-nums text-white">
              {loading ? "…" : k.value}
            </p>
          </article>
        ))}
      </section>

      <section className="rounded-xl border border-white/10 bg-slate-900/60 p-4 shadow-inner shadow-black/40">
        <h2 className="text-sm font-semibold text-white">Actions rapides</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setAccessManagerOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <Users className="h-4 w-4 text-emerald-400" />
            Gérer accès clients
            <ArrowUpRight className="h-3.5 w-3.5 opacity-60" />
          </button>
          <Link
            to="/admin/planificateur"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <Clock className="h-4 w-4 text-emerald-400" />
            Voir demandes de modification
          </Link>
          <Link
            to="/admin/interventions"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <ShieldAlert className="h-4 w-4 text-emerald-400" />
            Voir urgences client
          </Link>
          <Link
            to="/admin/finance/factures"
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800"
          >
            <Upload className="h-4 w-4 text-emerald-400" />
            Voir preuves paiement
          </Link>
          <button
            type="button"
            onClick={openPortal}
            className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-600/20 px-3 py-2 text-sm font-medium text-emerald-100 transition hover:bg-emerald-600/30"
          >
            <ExternalLink className="h-4 w-4" />
            Ouvrir lien portail client
          </button>
          <button
            type="button"
            disabled={!isAdmin}
            title={!isAdmin ? MSG_ADMIN : undefined}
            onClick={() => openLinkModal()}
            className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Link2 className="h-4 w-4 text-emerald-400" />
            Lier compte portail
          </button>
        </div>
        {!isAdmin ? (
          <p className="mt-3 text-xs text-amber-200/90">{MSG_ADMIN} pour créer / lier des comptes et modifier les accès.</p>
        ) : null}
      </section>

      <section className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/80 shadow-xl shadow-black/40">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-emerald-400" />
            <h2 className="text-sm font-semibold text-white">Clients portail</h2>
          </div>
          <button
            type="button"
            onClick={() => void loadTable()}
            className="text-xs font-medium text-emerald-400 hover:text-emerald-300"
          >
            Rafraîchir
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10 text-left text-sm">
            <thead className="bg-slate-900/90 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Téléphone</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Accès portail</th>
                <th className="px-4 py-3 font-medium">Dernière connexion</th>
                <th className="px-4 py-3 font-medium">Abonnement actif</th>
                <th className="px-4 py-3 font-medium">Comptes portail</th>
                <th className="px-4 py-3 font-medium">Demandes en attente</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 bg-slate-950/40">
              {tableLoading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                    Chargement…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                    Aucun client à afficher.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-white/5">
                    <td className="px-4 py-3 font-medium text-white">{r.nomAffiche}</td>
                    <td className="px-4 py-3 text-slate-300">{r.telephone}</td>
                    <td className="px-4 py-3 text-slate-300">{r.email}</td>
                    <td className="px-4 py-3">
                      {(r.liaisonsPortail ?? 0) === 0 ? (
                        <span className="rounded-full bg-slate-500/15 px-2 py-0.5 text-xs text-slate-400">
                          Sans compte
                        </span>
                      ) : (
                        <span
                          className={
                            r.accesPortail
                              ? "rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-300"
                              : "rounded-full bg-rose-500/15 px-2 py-0.5 text-xs text-rose-200"
                          }
                        >
                          {r.accesPortail ? "Activé" : "Désactivé"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {r.derniereConnexion
                        ? new Date(r.derniereConnexion).toLocaleString("fr-FR")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      {r.abonnementActif ? "Oui" : "Non"}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-200">
                      {r.liaisonsPortail ?? 0}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-200">
                      {r.demandesEnAttente}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.liaisonsPortail ?? 0) > 0 ? (
                          r.accesPortail ? (
                            <button
                              type="button"
                              disabled={!isAdmin || actionId === r.id}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => void setAccess(r.id, false)}
                              className="rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <ShieldOff className="mr-1 inline h-3 w-3" />
                              Désactiver
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={!isAdmin || actionId === r.id}
                              title={!isAdmin ? MSG_ADMIN : undefined}
                              onClick={() => void setAccess(r.id, true)}
                              className="rounded-md border border-emerald-500/30 bg-emerald-600/20 px-2 py-1 text-xs text-emerald-100 hover:bg-emerald-600/30 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <ShieldPlus className="mr-1 inline h-3 w-3" />
                              Activer
                            </button>
                          )
                        ) : null}
                        <button
                          type="button"
                          disabled={!isAdmin || actionId === r.id}
                          title={!isAdmin ? MSG_ADMIN : undefined}
                          onClick={() => openResetModal(r.id)}
                          className="rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <KeyRound className="mr-1 inline h-3 w-3" />
                          Reset MDP
                        </button>
                        <button
                          type="button"
                          disabled={!isAdmin}
                          title={!isAdmin ? MSG_ADMIN : undefined}
                          onClick={() => openLinkModal(r.id)}
                          className="rounded-md border border-emerald-500/30 bg-emerald-600/10 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-600/20 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Link2 className="mr-1 inline h-3 w-3" />
                          Lier compte
                        </button>
                        <Link
                          to={`/admin/clients/${r.id}`}
                          className="rounded-md border border-white/10 bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
                        >
                          Fiche client
                        </Link>
                        {import.meta.env.DEV ? (
                          <span
                            className="cursor-not-allowed rounded-md border border-dashed border-white/20 px-2 py-1 text-xs text-slate-500"
                            title="Connexion impersonnelle : à brancher (token client dédié)."
                          >
                            Voir comme client
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {linkModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-portal-title"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-slate-900 p-4 text-slate-100 shadow-2xl">
            <h3 id="link-portal-title" className="text-lg font-semibold text-white">
              Lier un compte portail
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              Associez un utilisateur <strong>CLIENT</strong> existant (orphelin) ou créez un compte
              dédié. Le lien est stocké dans <code className="text-emerald-300">User.clientId</code>
              .
            </p>
            {!isAdmin ? (
              <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-950/40 px-3 py-2 text-sm text-amber-100">
                {MSG_ADMIN}
              </p>
            ) : null}

            <label className="mt-4 block text-xs font-medium text-slate-400">
              Fiche client
              <select
                className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-2 py-2 text-sm disabled:opacity-50"
                disabled={!isAdmin}
                value={linkClientId ?? ""}
                onChange={(e) =>
                  setLinkClientId(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">— Choisir —</option>
                {rows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nomAffiche} (#{r.id})
                  </option>
                ))}
              </select>
            </label>

            {linkClientId ? (
              <>
                <div className="mt-4 rounded-lg border border-white/10 bg-slate-950/80 p-3 text-sm">
                  <p className="font-medium text-slate-200">Comptes déjà liés</p>
                  {linkedPortalUsers.length === 0 ? (
                    <p className="text-slate-500">Aucun.</p>
                  ) : (
                    <ul className="mt-1 list-inside list-disc text-slate-300">
                      {linkedPortalUsers.map((u) => (
                        <li key={u.id}>
                          {u.nom} — {u.email ?? "sans e-mail"} ({u.actif ? "actif" : "inactif"})
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-slate-200">Associer un compte existant</p>
                  <select
                    className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-2 text-sm disabled:opacity-50"
                    disabled={!isAdmin}
                    value={pickUserId}
                    onChange={(e) => setPickUserId(e.target.value)}
                  >
                    <option value="">— Utilisateur CLIENT sans fiche —</option>
                    {orphans.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.nom} ({u.email ?? "?"}) #{u.id}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={!isAdmin || linkBusy || !pickUserId}
                    onClick={() => void submitLinkExisting()}
                    className="w-full rounded-lg bg-emerald-600 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    Associer à cette fiche
                  </button>
                </div>

                <div className="mt-6 space-y-2 border-t border-white/10 pt-4">
                  <p className="text-sm font-medium text-slate-200">Créer un compte CLIENT</p>
                  <input
                    type="email"
                    placeholder="E-mail"
                    className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-2 text-sm disabled:opacity-50"
                    disabled={!isAdmin}
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    autoComplete="off"
                  />
                  <input
                    placeholder="Nom affiché"
                    className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-2 text-sm disabled:opacity-50"
                    disabled={!isAdmin}
                    value={createNom}
                    onChange={(e) => setCreateNom(e.target.value)}
                  />
                  <input
                    type="password"
                    placeholder="Mot de passe (min. 6)"
                    className="w-full rounded-md border border-white/10 bg-slate-950 px-2 py-2 text-sm disabled:opacity-50"
                    disabled={!isAdmin}
                    value={createPass}
                    onChange={(e) => setCreatePass(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    disabled={!isAdmin || linkBusy}
                    onClick={() => void submitCreatePortalUser()}
                    className="w-full rounded-lg border border-white/15 bg-slate-800 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    Créer et lier
                  </button>
                </div>
              </>
            ) : null}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLinkModalOpen(false)}
                className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {resetTargetId ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/65 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-pwd-title"
        >
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-4 text-slate-100 shadow-2xl">
            <h3 id="reset-pwd-title" className="text-lg font-semibold text-white">
              Réinitialiser mot de passe
            </h3>
            <p className="text-sm text-slate-400">Client #{resetTargetId}</p>
            <p className="mt-3 rounded-lg border border-emerald-500/25 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-100/90">
              <span className="font-medium text-emerald-300">mustChangePassword</span> : le serveur
              forcera le changement de mot de passe à la prochaine connexion portail.
            </p>
            <label className="mt-4 block text-xs font-medium text-slate-400">
              Mot de passe temporaire
              <input
                type="password"
                className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-slate-400">
              Confirmation
              <input
                type="password"
                className="mt-1 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white"
                value={resetPwdConfirm}
                onChange={(e) => setResetPwdConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setResetTargetId(null);
                  setResetPwd("");
                  setResetPwdConfirm("");
                }}
                className="rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-white/5"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!isAdmin || actionId === resetTargetId}
                title={!isAdmin ? MSG_ADMIN : undefined}
                onClick={() => void submitResetModal()}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ClientPortalAccessManager
        open={accessManagerOpen}
        onClose={() => setAccessManagerOpen(false)}
        onDidChange={() => {
          void loadSummary();
          void loadTable();
        }}
      />
    </div>
  );
}
